import { useState, useEffect, useCallback } from "react";
import { supabase } from "../lib/supabase";

export type CompetitionMode = "rep_vs_rep" | "threshold";
type Sector = "closer" | "setter" | "dialer";

export interface LeaderboardSetting {
  id?: string;
  organization_id: string;
  role: Sector;
  competition_mode: CompetitionMode;
  gold_threshold: number;
  silver_threshold: number;
  bronze_threshold: number;
}

export function useLeaderboardSettings(organizationId: string | null | undefined) {
  const [settings, setSettings] = useState<Map<Sector, LeaderboardSetting>>(new Map());
  const [isLoading, setIsLoading] = useState(false);

  const fetchSettings = useCallback(async () => {
    if (!organizationId || !supabase) return;
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("leaderboard_settings")
        .select("*")
        .eq("organization_id", organizationId);

      if (error) {
        console.warn("Failed to fetch leaderboard settings:", error);
        return;
      }

      const map = new Map<Sector, LeaderboardSetting>();
      for (const row of data || []) {
        map.set(row.role as Sector, row as LeaderboardSetting);
      }
      setSettings(map);
    } finally {
      setIsLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const saveSetting = useCallback(async (setting: Omit<LeaderboardSetting, "id">) => {
    if (!supabase) return;
    const { data, error } = await supabase
      .from("leaderboard_settings")
      .upsert(
        {
          organization_id: setting.organization_id,
          role: setting.role,
          competition_mode: setting.competition_mode,
          gold_threshold: setting.gold_threshold,
          silver_threshold: setting.silver_threshold,
          bronze_threshold: setting.bronze_threshold,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "organization_id,role" }
      )
      .select()
      .single();

    if (error) {
      console.error("Failed to save leaderboard setting:", error);
      return;
    }

    setSettings((prev) => {
      const next = new Map(prev);
      next.set(setting.role, data as LeaderboardSetting);
      return next;
    });
  }, []);

  const getMode = useCallback(
    (role: Sector): CompetitionMode => {
      return settings.get(role)?.competition_mode ?? "rep_vs_rep";
    },
    [settings]
  );

  const getThresholds = useCallback(
    (role: Sector) => {
      const s = settings.get(role);
      return {
        gold: s?.gold_threshold ?? 0,
        silver: s?.silver_threshold ?? 0,
        bronze: s?.bronze_threshold ?? 0,
      };
    },
    [settings]
  );

  return { settings, isLoading, saveSetting, getMode, getThresholds, refetch: fetchSettings };
}
